// The role × permission matrix.
//
// Edits are staged locally and saved per role rather than on every checkbox,
// so a half-finished change never lands — and so the backend's refusals (like
// stripping users.manage from the admin role) surface against a clear action.

import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Lock, Save, Undo2 } from 'lucide-react';
import { accessApi, type Permission, type Role } from '../../lib/api';

type Props = {
    roles: Role[];
    permissionsByCategory: [string, Permission[]][];
    onChanged: () => Promise<void>;
};

export default function RolesTab({ roles, permissionsByCategory, onChanged }: Props) {
    const [selectedRoleId, setSelectedRoleId] = useState<number | null>(roles[0]?.id ?? null);
    const [draft, setDraft] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savedAt, setSavedAt] = useState<number | null>(null);

    const role = useMemo(
        () => roles.find((r) => r.id === selectedRoleId) ?? null,
        [roles, selectedRoleId],
    );

    // Reset the draft whenever the selected role changes or the server data is
    // reloaded, so the checkboxes always start from persisted truth.
    useEffect(() => {
        setDraft(new Set(role?.permissions ?? []));
        setError(null);
    }, [role]);

    const dirty = useMemo(() => {
        const original = new Set(role?.permissions ?? []);
        if (original.size !== draft.size) return true;
        for (const key of draft) if (!original.has(key)) return true;
        return false;
    }, [draft, role]);

    const toggle = (key: string) => {
        setDraft((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
        setSavedAt(null);
    };

    const save = async () => {
        if (!role || saving) return;
        setSaving(true);
        setError(null);
        try {
            await accessApi.setRolePermissions(role.id, [...draft]);
            await onChanged();
            setSavedAt(Date.now());
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex gap-6 items-start">
            {/* Role picker */}
            <aside className="w-60 flex-shrink-0 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                {roles.map((r) => {
                    const active = r.id === selectedRoleId;
                    return (
                        <button
                            key={r.id}
                            onClick={() => setSelectedRoleId(r.id)}
                            className={[
                                'w-full text-left px-4 py-3 border-b border-gray-100 dark:border-slate-700/60 last:border-0 transition-colors',
                                active
                                    ? 'bg-indigo-50 dark:bg-indigo-500/10'
                                    : 'hover:bg-gray-50 dark:hover:bg-slate-700/40',
                            ].join(' ')}
                        >
                            <div className="flex items-center gap-1.5">
                                <span
                                    className={[
                                        'text-sm font-medium',
                                        active
                                            ? 'text-indigo-700 dark:text-indigo-300'
                                            : 'text-gray-900 dark:text-gray-100',
                                    ].join(' ')}
                                >
                                    {r.name}
                                </span>
                                {r.is_system && (
                                    // Lucide icons take no `title` prop, so the
                                    // tooltip lives on a wrapper.
                                    <span title="Built-in role — cannot be deleted" className="flex">
                                        <Lock size={11} className="text-gray-400" />
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {r.permissions.length} permissions · {r.assigned_users} user
                                {r.assigned_users === 1 ? '' : 's'}
                            </div>
                        </button>
                    );
                })}
            </aside>

            {/* Permission matrix for the selected role */}
            <section className="flex-1 min-w-0 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
                {!role ? (
                    <p className="p-6 text-sm text-gray-500">Select a role.</p>
                ) : (
                    <>
                        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200 dark:border-slate-700">
                            <div className="min-w-0">
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                                    {role.name}
                                </h2>
                                {role.description && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                        {role.description}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {savedAt && !dirty && (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                        <Check size={13} /> Saved
                                    </span>
                                )}
                                {dirty && (
                                    <button
                                        onClick={() => setDraft(new Set(role.permissions))}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
                                    >
                                        <Undo2 size={14} /> Revert
                                    </button>
                                )}
                                <button
                                    onClick={save}
                                    disabled={!dirty || saving}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {saving ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                        <Save size={14} />
                                    )}
                                    Save changes
                                </button>
                            </div>
                        </header>

                        {error && (
                            <p
                                role="alert"
                                className="mx-5 mt-4 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 rounded-md px-3 py-2"
                            >
                                {error}
                            </p>
                        )}

                        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            {permissionsByCategory.map(([category, perms]) => (
                                <div key={category}>
                                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                                        {category}
                                    </h3>
                                    <div className="space-y-1.5">
                                        {perms.map((p) => (
                                            <label
                                                key={p.key}
                                                className="flex items-start gap-2.5 px-2 py-1.5 -mx-2 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700/40 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={draft.has(p.key)}
                                                    onChange={() => toggle(p.key)}
                                                    className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <span className="min-w-0">
                                                    <span className="block text-sm text-gray-800 dark:text-gray-200">
                                                        {p.name}
                                                    </span>
                                                    {p.description && (
                                                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                                                            {p.description}
                                                        </span>
                                                    )}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </section>
        </div>
    );
}
